import {Input, Select} from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import {course_getSubjects_Api, medal_getMedals_Api} from 'apis'
import {getId} from 'apis/model/base'
import ImageLoader from 'components/ImageLoader'
import Pure from 'components/Pure'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {ActionModalWrapper} from 'components/form/ActionModalWrapper'
import {createValue} from 'components/form/utils'
import {lengthBetween, noDoubleSpaces, noLeadingTrailingSpaces, required, runRules,} from 'components/form/ValidateForm'
import {Formik} from 'formik'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {mapEntityOptions} from 'views/AppLocals/factory/createSelectEntityProps'
import {createControlledFormField, createNumberControlledInput} from 'views/AppLocals/factory/createFormField'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import SlateEditor from 'views/SlateEditor'
import {ModalEditorToolbar} from 'views/SlateEditor/SlateEditor'
import {convertFromString, convertToString} from 'views/SlateEditor/functions'
import {dropdownComponents} from "../../custom/ReactSelectComponents";
import {ChipGroup, Section} from "components/form/ModernForm";

// ─── Debounced inputs ─────────────────────────────────────────────────────────

const DebouncedInput = ({name, value, onChange, placeholder, ...rest}) => {
    const [local, setLocal] = useState(value ?? '')
    const debounced = useRef(_.debounce((val) => onChange(createValue(name, val)), 280)).current

    useEffect(() => { setLocal(value ?? '') }, [value])

    const handleChange = useCallback(
        (e) => { setLocal(e.target.value); debounced(e.target.value) },
        [debounced]
    )

    return <Input value={local} onChange={handleChange} placeholder={placeholder} {...rest}/>
}

const DebouncedTextArea = ({name, value, onChange, placeholder, ...rest}) => {
    const [local, setLocal] = useState(value ?? '')
    const debounced = useRef(_.debounce((val) => onChange(createValue(name, val)), 280)).current

    useEffect(() => { setLocal(value ?? '') }, [value])

    const handleChange = useCallback(
        (e) => { setLocal(e.target.value); debounced(e.target.value) },
        [debounced]
    )

    return <TextArea value={local} onChange={handleChange} placeholder={placeholder} {...rest}/>
}

// ─── Field components ─────────────────────────────────────────────────────────

const CurriculumTitleField = ({name, value, onChange}) => {
    const t = useTranslate()
    return (
        <DebouncedInput
            name={name}
            value={value}
            onChange={onChange}
            placeholder={t('curriculum name')}
            showCount
            maxLength={180}
        />
    )
}

const CurriculumDescriptionField = ({name, value, onChange}) => {
    const t = useTranslate()
    return (
        <DebouncedTextArea
            name={name}
            value={value}
            onChange={onChange}
            placeholder={t('description')}
            className="scrollBarThin"
            showCount
            maxLength={500}
            autoSize={{minRows: 3, maxRows: 5}}
        />
    )
}

// Loads medals of the owner, shows avatar + title in dropdown.
const DefaultMedalField = ({name, value, onChange, form}) => {
    const t = useTranslate()
    const owner_id = _.get(form, 'values.owner_id')
    const owner_type = _.get(form, 'values.owner_type')

    const {response, isLoading} = useAsyncWithCache({
        apiInfo: medal_getMedals_Api,
        query: bindQueryParams([{prop: owner_type}, {id: owner_id}]),
        cacheId: `${medal_getMedals_Api.path}/${owner_type}/${owner_id}`,
    })

    const medals = _.get(response, 'data.data', [])

    return (
        <Select
            loading={isLoading}
            className="w-full"
            placeholder={t('click to select')}
            value={value || undefined}
            allowClear
            showSearch
            filterOption={(input, option) =>
                (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
            }
            suffixIcon={<dropdownComponents.DropdownIndicator/>}
            onChange={(v) => onChange(createValue(name, v ?? null))}
            options={medals.map((m) => ({
                value: m.id,
                searchText: m.title,
                label: (
                    <div className="flex items-center gap-2">
                        <ImageLoader src={m.avatar} style={{width: 26, height: 20, objectFit: 'contain'}}/>
                        <span className="text-sm">{m.title}</span>
                    </div>
                ),
            }))}
        />
    )
}

// ─── Form schemas ─────────────────────────────────────────────────────────────

const baseFormSchema = {
    title: {
        children: [{label: 'curriculum name', name: 'title', isRequired: true, component: CurriculumTitleField}],
    },
    description: {
        title: 'description',
        children: [{name: 'description', component: CurriculumDescriptionField}],
    },
    // Thời gian + Tổng môn học — 2 cột cùng hàng
    durationAndSubjects: {
        inline: true,
        className: 'flex gap-3',
        itemClassNames: ['flex-1', 'flex-1'],
        children: [
            {
                name: 'duration_months',
                label: 'duration (months)',
                component: createNumberControlledInput({placeholder: 'ex: 12', min: 1, max: 120}),
            },
            {
                name: 'subject_count',
                label: 'total subjects',
                component: createNumberControlledInput({placeholder: 'ex: 10', min: 0, disabled: true}),
            },
        ],
    },
    default_medal_id: {
        title: 'default certificate',
        children: [{name: 'default_medal_id', component: DefaultMedalField}],
    },
    about: {
        title: 'about curriculum',
        children: [
            {
                name: 'about',
                component: createControlledFormField({
                    Component: SlateEditor,
                    ToolbarComponent: ModalEditorToolbar,
                    withProps: ({value}) => ({defaultValue: value}),
                    withValues: ({resetKey}) => ({resetKey}),
                }),
            },
        ],
    },
}

// ─── Subject param schemas ─────────────────────────────────────────────────────

const MandatorySelect = ({name, value, onChange}) => {
    const t = useTranslate()
    return (
        <ChipGroup
            options={[
                {label: t('is mandatory'), value: 1},
                {label: t('optional'), value: 0},
            ]}
            value={value}
            onChange={(v) => onChange(createValue(name, v))}
        />
    )
}

const subjectParamSchema = {
    is_mandatory: {
        title: 'is mandatory',
        children: [{name: 'is_mandatory', component: MandatorySelect}],
    },
    // Số buổi học + Điểm đạt — 2 cột cùng hàng
    sessionsAndScore: {
        inline: true,
        className: 'flex gap-3',
        itemClassNames: ['flex-1', 'flex-1'],
        children: [
            {
                name: 'number_of_sessions',
                label: 'number of sessions',
                component: createNumberControlledInput({placeholder: 'ex: 30', min: 1, disabled: true}),
            },
            {
                name: 'passed_score',
                label: 'passed score',
                component: createNumberControlledInput({placeholder: 'ex: 5', min: 0}),
            },
        ],
    },
}

// ─── Validation ───────────────────────────────────────────────────────────────

const validateBase = (t) => (values) => {
    const {title} = values ?? {}
    const msg = runRules(title, [
        required(t),
        lengthBetween(2, 180, t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean))
    return msg ? {title: msg} : {}
}

// ─── Create Curriculum ────────────────────────────────────────────────────────

export const CreateCurriculumModal = ({
    owner_id,
    owner_type,
    onSubmit = NullAsDefault,
    onCancel = NullAsDefault,
}) => {
    const t = useTranslate()

    const initialValues = useMemo(() => ({
        owner_id,
        owner_type,
        title: '',
        description: '',
        duration_months: 1,
        subject_count: 0,
        about: undefined,
        default_medal_id: undefined,
    }), [owner_id, owner_type])

    return (
        <Formik
            validate={validateBase(t)}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={(values) => {
                const {owner_id: _oid, owner_type: _otype, about, resetKey, ...rest} = values ?? {}
                onSubmit(_.omitBy({
                    ...rest,
                    about: about ? convertToString(about) : undefined,
                }, (v) => v === '' || v === undefined))
            }}>
            <ActionModalWrapper
                className="gap-4"
                action={<FormActionGroups submitProps={{label: 'create'}}/>}>
                <Section title={t('basic information')}>
                    <FieldsFactory
                        formSchema={[
                            baseFormSchema.title,
                            baseFormSchema.description,
                        ]}
                    />
                </Section>
                <Section title={t('curriculum settings')}>
                    <FieldsFactory formSchema={[baseFormSchema.durationAndSubjects]}/>
                    <Pure>
                        <FieldsFactory formSchema={[baseFormSchema.default_medal_id]}/>
                    </Pure>
                </Section>
                <Section>
                    <FieldsFactory formSchema={[baseFormSchema.about]}/>
                </Section>
            </ActionModalWrapper>
        </Formik>
    )
}

// ─── Edit Curriculum ──────────────────────────────────────────────────────────

export const EditCurriculumModal = ({
    owner_id,
    owner_type,
    initialValues = {},
    onSubmit = NullAsDefault,
    onCancel = NullAsDefault,
}) => {
    const t = useTranslate()

    const _initialValues = useMemo(() => {
        const {about, ...rest} = initialValues ?? {}
        return {
            duration_months: 1,
            subject_count: 0,
            ...rest,
            owner_id,
            owner_type,
            title: rest?.title ?? '',
            description: rest?.description ?? '',
            about: convertFromString(about),
            default_medal_id: rest?.default_medal?.id ?? null,
        }
    }, [initialValues, owner_id, owner_type])

    return (
        <Formik
            validate={validateBase(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                const {owner_id: _oid, owner_type: _otype, about, resetKey, default_medal: _dm, ...rest} = changes ?? {}
                onSubmit(_.omitBy({
                    ...rest,
                    about: about ? convertToString(about) : undefined,
                }, _.isUndefined))
            }}>
            <ActionModalWrapper
                className="gap-4"
                action={<FormActionGroups submitProps={{label: 'save'}}/>}>
                <Section title={t('basic information')}>
                    <FieldsFactory
                        formSchema={[
                            baseFormSchema.title,
                            baseFormSchema.description,
                        ]}
                    />
                </Section>
                <Section title={t('curriculum settings')}>
                    <FieldsFactory formSchema={[baseFormSchema.durationAndSubjects]}/>
                    <Pure>
                        <FieldsFactory formSchema={[baseFormSchema.default_medal_id]}/>
                    </Pure>
                </Section>
                <Section>
                    <FieldsFactory formSchema={[baseFormSchema.about]}/>
                </Section>
            </ActionModalWrapper>
        </Formik>
    )
}

// ─── Link Subject ─────────────────────────────────────────────────────────────

export const LinkSubjectModal = ({
    curriculum_id,
    owner_id,
    owner_type,
    onSubmit = NullAsDefault,
    onCancel = NullAsDefault,
}) => {
    const t = useTranslate()

    // Danh sách môn học của chủ sở hữu có thể vượt quá 1 trang (BE mặc định 10 item/trang) nên
    // không thể tải hết 1 lần rồi lọc client-side như trước — chuyển sang search server thật:
    // gõ tới đâu gọi lại API với keyword tới đó (khớp API `keyword` có sẵn ở SubjectController),
    // debounce theo đúng pattern DebouncedInput/ContainerPicker đang dùng trong app.
    const query = useMemo(
        () => bindQueryParams([{id: owner_id}, {type: owner_type}]),
        [owner_id, owner_type]
    )

    const {response, isLoading, handleAsyncAction: search} = useAsyncAction({
        apiInfo: course_getSubjects_Api,
        query,
    })

    useEffect(() => {
        search()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query])

    const handleSearch = useRef(_.debounce((keyword) => search({keyword}), 300)).current

    const subjects = _.get(response, 'data.data', [])
    const subjectOptions = mapEntityOptions(subjects, {getValue: getId, getLabel: getTitle})

    const initialValues = useMemo(() => ({
        curriculum_id,
        subject_id: undefined,
        is_mandatory: 1,
        number_of_sessions: 1,
        passed_score: 5,
    }), [curriculum_id])

    const validate = (values) => {
        const errors = {}
        if (!values?.subject_id) errors.subject_id = t('required field')
        return errors
    }

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={(values) => {
                const {subject_id, curriculum_id: cid, ...params} = values ?? {}
                onSubmit(_.omitBy(params, _.isUndefined), {curriculum_id: cid, subject_id})
            }}>
            {({values, setFieldValue, setFieldTouched, errors, touched}) => (
                <ActionModalWrapper
                    className="gap-4"
                    action={<FormActionGroups submitProps={{label: 'link'}}/>}>
                    {/* Subject select — manual render để show validation. Lỗi chỉ hiện khi field đã
                        "touched" (blur khỏi Select hoặc đã chọn option) - khớp cơ chế touched-gate
                        ở FieldsFactory.js, tránh đập lỗi "required" ngay khi vừa mở modal. */}
                    <div
                        className="flex flex-col gap-1"
                        onBlur={() => setFieldTouched('subject_id', true, false)}>
                        <label className="text-xs font-medium text-color-200 tracking-wider">
                            {t('subject')}
                            <span className="ml-1 text-rose-600">*</span>
                        </label>
                        <Select
                            loading={isLoading}
                            className="w-full"
                            placeholder={t('select subject')}
                            suffixIcon={<dropdownComponents.DropdownIndicator/>}
                            value={values.subject_id}
                            options={subjectOptions}
                            showSearch
                            filterOption={false}
                            onSearch={handleSearch}
                            onChange={(v) => {
                                const subject = subjects.find((item) => getId(item) === v)

                                setFieldValue('subject_id', v)
                                setFieldValue(
                                    'number_of_sessions',
                                    subject?.number_of_sessions ?? 1
                                )
                                setFieldTouched('subject_id', true, false)
                            }}
                        />
                        {touched.subject_id && errors.subject_id && (
                            <div
                                style={{marginTop: 4, fontSize: 11, minHeight: '1em'}}
                                className="ml-1 text-rose-600 italic error-message error-message-show">
                                {errors.subject_id}
                            </div>
                        )}
                    </div>
                    <FieldsFactory
                        formSchema={[
                            subjectParamSchema.is_mandatory,
                            subjectParamSchema.sessionsAndScore,
                        ]}
                    />
                </ActionModalWrapper>
            )}
        </Formik>
    )
}

// ─── Edit Subject Params ───────────────────────────────────────────────────────

export const EditSubjectParamModal = ({
    initialValues = {},
    onSubmit = NullAsDefault,
    onCancel = NullAsDefault,
}) => {
    const _initialValues = useMemo(() => ({
        is_mandatory: 1,
        number_of_sessions: 1,
        passed_score: 5,
        ...initialValues,
    }), [initialValues])

    return (
        <Formik
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                onSubmit(_.omitBy(changes, _.isUndefined))
            }}>
            <ActionModalWrapper
                className="gap-4"
                action={<FormActionGroups submitProps={{label: 'save'}}/>}>
                <FieldsFactory
                    formSchema={[
                        subjectParamSchema.is_mandatory,
                        subjectParamSchema.sessionsAndScore,
                    ]}
                />
            </ActionModalWrapper>
        </Formik>
    )
}
